/**
 * Seed / migration defaults for core-behavior scenarios.
 * Written into bots/{botId}/flows/{flowId}.scenarios and linked actions.
 * UI (edit-behavior) must read from Firestore, not from this file.
 */

/** scenario.description values keyed by behavior name → scenario name */
export const BEHAVIOR_SCENARIO_DESCRIPTIONS: Record<
  string,
  Record<string, string>
> = {
  'Welcome Message': {
    'Proactive Welcome': 'Sends a personalized greeting to first-time visitors.',
    'Proactive Welcome (Returning User)':
      'Welcomes returning users and suggests resuming or exploring.',
    'Reactive Welcome':
      'Welcomes new users with a greeting while the bot is inactive.',
  },
  'Bot Invocation Response': {
    'Bot Invoked While Deactivated':
      'Responds when a user tries to interact with a deactivated bot.',
    'Bot Summoned While Disabled':
      'Responds when a user tries to interact with a deactivated bot.',
  },
  'Handover Flow': {
    'Email Missing':
      "Asks for the user's email when contacting support.",
    'Email Invalid':
      'Explains that the provided email format is incorrect and asks for a valid one.',
    'Handover System Error':
      'Apologizes for the technical issue and invites the user to retry.',
    'Handover Success': 'Confirms that the handover request has been sent.',
    'Live Chat Ready':
      'Notifies the user that a human agent is joining the conversation.',
    'Generic Handover Request':
      'Handles any handover request that does not match a specific case.',
    'System Error':
      'Apologizes for the technical issue and invites the user to retry.',
    'Default Scenario':
      'Handles any handover request that does not match a specific case.',
    'Handover Requested':
      'Handles any handover request that does not match a specific case.',
  },
  'Data Deletion': {
    'Ask Confirmation':
      'Asks the user to confirm permanent data deletion with Yes or No.',
    Confirmed:
      'Runs after the user confirms Yes and the deletion request has been accepted.',
    Cancelled: 'Confirms that no data was deleted when the user chose No.',
    Error: 'Shown when the deletion request fails after the user confirmed Yes.',
  },
  Data_Deletion: {
    'Ask Confirmation':
      'Asks the user to confirm permanent data deletion with Yes or No.',
    Confirmed:
      'Runs after the user confirms Yes and the deletion request has been accepted.',
    Cancelled: 'Confirms that no data was deleted when the user chose No.',
    Error: 'Shown when the deletion request fails after the user confirmed Yes.',
  },
  'Information Not Found': {
    'Information Not Found':
      'Acknowledges when no relevant information is found.',
    'Information Not Found - Offer Human':
      'Runs once the configured number of unanswered questions is reached, and offers to bring in a human.',
  },
  'Fallback Response': {
    'User Not Understood':
      "Asks for clarification when the user's input is unclear.",
    'Information Not Found':
      'Acknowledges when no relevant information is found.',
    'Information Not Found - Offer Human':
      'Runs once the configured number of unanswered questions is reached, and offers to bring in a human.',
    'Off-Topic Request': 'Gently redirects off-topic conversations.',
    'Security & Policy Violation':
      'Refuses policy violations and explains the limitation.',
    'API / Tool Error':
      'Notifies users gracefully when an API or tool error occurs.',
    API_TOOL_ERROR:
      'Notifies users gracefully when an API or tool error occurs.',
    FATAL_ERROR:
      'Triggered on fatal system errors, including when the plan limit is reached and the bot is blocked. Plays a brief apology and asks the user to try again later.',
    'No Reply':
      'Triggered by Call Manager after a period of silence. Re-engages the caller to confirm they are still on the line.',
  },
  Error: {
    API_TOOL_ERROR:
      'Triggered when a tool or function call fails. Reply without exposing internal error details.',
    FATAL_ERROR:
      'Triggered on fatal system errors, including when the plan limit is reached and the bot is blocked. Plays a brief apology and asks the user to try again later.',
  },
  'End Conversation': {
    CONVERSATION_ENDED:
      'The caller has clearly ended the conversation (goodbye / thanks) but may not have hung up. Warmly acknowledge and say a brief farewell.',
    TASK_COMPLETED:
      'The conversation has reached a natural conclusion. Thank the user, confirm the session is ending, and disconnect the call.',
    NO_INPUT_EXHAUSTED:
      'No response was detected after all no-input re-prompts. Inform the caller and disconnect the call.',
    MAX_DURATION_REACHED:
      'The maximum call duration has been reached. Inform the caller, thank them for their time, and disconnect the call.',
  },
};

export const SCENARIO_RENAME_MAP: Record<string, string> = {
  'System Error': 'Handover System Error',
  'Default Scenario': 'Generic Handover Request',
  'Bot Summoned While Disabled': 'Bot Invoked While Deactivated',
  'Fallback Type: API / Tool Error': 'API / Tool Error',
};

/** Allowed handover scenarios for channel bots. */
export const ALLOWED_HANDOVER_SCENARIOS = new Set([
  'Handover Success',
  'Live Chat Ready',
  'Generic Handover Request',
  'Default Scenario',
  'Handover Requested',
]);

const CHANNEL_BOT_TYPES = new Set([
  'widget-chat',
  'widget_chat',
  'widgetchat',
  'whatsapp',
  'instagram',
  'messenger',
  'facebook',
]);

/**
 * Whether a Handover Flow scenario should be shown for the botType.
 * widget-chat / whatsapp / instagram / messenger →
 * Handover Success + Live Chat Ready + Generic Handover Request.
 */
export function isHandoverScenarioVisibleForBotType(
  scenarioName: string,
  botType: string | null | undefined,
): boolean {
  const normalized = String(botType || '')
    .trim()
    .toLowerCase();
  if (!CHANNEL_BOT_TYPES.has(normalized)) {
    return true;
  }
  return ALLOWED_HANDOVER_SCENARIOS.has((scenarioName || '').trim());
}

export const SCENARIO_DISPLAY_NAME_MAP: Record<string, string> = {
  API_TOOL_ERROR: 'API / Tool Error (general)',
  FATAL_ERROR: 'Fatal Error',
  CONVERSATION_ENDED: 'conversation_ended (goodbye)',
  TASK_COMPLETED: 'task_completed',
  NO_INPUT_EXHAUSTED: 'Silence Timeout (no_input_exhausted)',
  MAX_DURATION_REACHED: 'Time Limit Reached (max_duration_reached)',
};

export const HANDOVER_INSTRUCTION_REPLACEMENTS: Record<
  string,
  { legacy: string[]; updated: string }
> = {
  'Email Missing': {
    legacy: [
      'Inform the user that to contact support, an email is required. Ask for it politely and explain that it will be used to send them support information.',
    ],
    updated:
      'Inform the user that an email address is required to contact support. Ask for it politely and explain that it will be used to send support updates.',
  },
  'Handover Success': {
    legacy: [
      'Successfully sent! Confirm to the user that their request is on its way and they will receive an email shortly. Be warm and reassuring.',
    ],
    updated:
      'Confirm that the request has been sent and let the user know they will receive an email shortly. Be warm and reassuring.',
  },
  'Live Chat Ready': {
    legacy: [
      'The bot is now off. Warmly inform the user that a human agent is being notified and will join the conversation soon. Thank them for their patience.',
    ],
    updated:
      'Inform the user that a human agent has been notified and will join the conversation soon. Thank them for their patience.',
  },
};

export const FALLBACK_SCENARIO_DEFAULT_INSTRUCTIONS: Record<string, string> = {
  'Off-Topic Request':
    "Generate a polite message when the user's request is outside the assistant's primary scope or domain. Clearly state that this topic falls outside the scope of what you can assist with and suggest an alternative way to obtain help, such as contacting a live agent.",
  'No Reply':
    'The user has not responded after a period of silence. Call Manager has triggered this scenario to re-engage the caller. Politely check whether the user is still on the line and invite them to continue the conversation. Keep the message brief, friendly, and non-intrusive.',
};

export const END_CONVERSATION_SCENARIO_DEFAULT_INSTRUCTIONS: Record<string, string> =
  {
    CONVERSATION_ENDED:
      'The caller has clearly ended the conversation (said goodbye / thanks) but may not have hung up. Warmly acknowledge and say a brief farewell before the call ends. One short TTS sentence.',
    TASK_COMPLETED:
      'The conversation has reached a natural conclusion. The user has indicated they are finished (e.g., goodbye, thank you, no further assistance needed), but the call is still active. Politely thank the user, confirm that the session is ending, and inform them that the call will now be disconnected. Then trigger the end_call function.',
    NO_INPUT_EXHAUSTED:
      'The user has not responded after all configured no-input re-prompts have been exhausted. Assume the caller is unavailable or has left the call. Inform the user that no response was detected and that the call will now be disconnected. Then proceed with hangup.',
    MAX_DURATION_REACHED:
      'The maximum allowed call duration has been reached. Politely inform the user that the session time limit has been reached and that the call must now end. Thank them for their time and proceed with hangup.',
  };

/** Analyze-invocation prompt on PAYLOAD_DISABLED_BOT_SUMMONED (classifierInstruction). */
export const BOT_INVOCATION_CLASSIFIER_INSTRUCTION = `--- Task: Analyze Invocation ---
The bot is inactive (StandBy or a live-chat handover to a human agent).
Judge ONLY the last user message. Ignore earlier invocation-check outcomes, insults, and "NO ANSWER IS REQUIRED" turns — they must not veto a new invocation.

Your names: {{bot_name}} and Conversell.

INVOKING YOU — answer normally. Do NOT return NO ANSWER IS REQUIRED:
* The last message addresses you by name with a greeting, question, or request
  (e.g. "hi {{bot_name}}", "hello {{bot_name}}", "hey Conversell", "{{bot_name}} help me", "{{bot_name}}, where is my order?").
* The user explicitly asks the bot to come back or take over (e.g. "bot take over", "talk to the bot").
* A greeting that includes your name IS an invocation. Reply with a short hello and help them.

SILENCE — return ONLY the exact string: \`NO ANSWER IS REQUIRED\`:
* Bare greeting with no bot name: "hi", "hello", "ok", "thanks", "any update?"
* Talking to the human agent, not you.
* Talking ABOUT you, not TO you (e.g. "your bot {{bot_name}} is stupid", "who is {{bot_name}}?").
* Name + insult/dismissal only, with no real question or request (e.g. "{{bot_name}} you are stupid", "{{bot_name}} shut up").`;

export const BOT_INVOCATION_RESPONSE_ACTION_ID = 'PAYLOAD_DISABLED_BOT_SUMMONED';

export const ERROR_SCENARIO_DEFAULT_INSTRUCTIONS: Record<string, string> = {
  API_TOOL_ERROR:
    "A tool/function call failed (you receive status/code/message). Reply to the caller without exposing the raw error or any internal detail. If it indicates missing/invalid input, ask the caller for it so it can be retried; otherwise briefly apologize and offer to retry or hand off to a human. One short spoken sentence, in the caller's language.",
  FATAL_ERROR:
    "Sorry, we're temporarily unable to respond right now (for example due to a system issue or plan limit reached). Please try again later.",
};

export const CALLBOT_FATAL_ERROR_INSTRUCTION =
  "Sorry, we're temporarily unable to respond. Please try again later.";

export const CALLBOT_FATAL_ERROR_DESCRIPTION =
  'Triggered on fatal system errors. Plays a brief apology and asks the caller to try again later.';

export const CALLBOT_HANDOVER_SCENARIO_DEFAULT_INSTRUCTIONS: Record<string, string> =
  {
    'Handover Requested':
      'When the caller asks for a human agent or you cannot resolve their request, produce a short reassuring spoken message (message_to_user) and trigger transfer_call to hand the caller over to a colleague via SIP REFER.',
    'Handover System Error':
      'Apologize for the technical issue that prevented the handover transfer. Briefly invite the caller to try again later or continue resolving their request without escalating.',
  };

/** Resolve displayName to persist on a scenario document. */
export function resolveScenarioDisplayName(scenarioName: string): string {
  return SCENARIO_DISPLAY_NAME_MAP[scenarioName] || scenarioName;
}

/** Resolve description to persist on a scenario document. */
export function resolveScenarioDescription(
  behaviorName: string,
  scenarioName: string,
  isCallbot = false,
): string {
  if (
    isCallbot &&
    scenarioName === 'FATAL_ERROR' &&
    (behaviorName === 'Fallback Response' || behaviorName === 'Error')
  ) {
    return CALLBOT_FATAL_ERROR_DESCRIPTION;
  }

  const byBehavior = BEHAVIOR_SCENARIO_DESCRIPTIONS[behaviorName] || {};
  if (byBehavior[scenarioName]) {
    return byBehavior[scenarioName];
  }

  // Fallback Response UI can show Error scenarios for super-admins
  if (behaviorName === 'Fallback Response') {
    return BEHAVIOR_SCENARIO_DESCRIPTIONS['Error']?.[scenarioName] || '';
  }

  return '';
}
